import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../services/api';
import { Instance, LoginMetrics, TransactionsByTypeEntry } from '../types';
import './InstanceDashboardPage.css';

const CHART_COLORS = [
  '#4a7729', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
  '#00BCD4', '#795548', '#607D8B', '#E91E63', '#009688',
  '#FFC107', '#3F51B5', '#8BC34A', '#FF5722', '#673AB7',
];

type PeriodKey = 'last1h' | 'last24h' | 'last7d' | 'last30d' | 'last360d' | 'custom';

interface PeriodOption {
  key: PeriodKey;
  label: string;
  groupBy: 'minute15' | 'hour' | 'day' | 'month';
  offsetMs: number;
}

const PERIOD_OPTIONS: PeriodOption[] = [
  { key: 'last1h',   label: 'Last Hour',     groupBy: 'minute15', offsetMs: 60 * 60 * 1000 },
  { key: 'last24h',  label: 'Last 24 Hours', groupBy: 'hour',     offsetMs: 24 * 60 * 60 * 1000 },
  { key: 'last7d',   label: 'Last 7 Days',   groupBy: 'day',      offsetMs: 7 * 24 * 60 * 60 * 1000 },
  { key: 'last30d',  label: 'Last Month',    groupBy: 'day',      offsetMs: 30 * 24 * 60 * 60 * 1000 },
  { key: 'last360d', label: 'Last 360 Days', groupBy: 'month',    offsetMs: 360 * 24 * 60 * 60 * 1000 },
];

function getPeriodParams(
  period: PeriodKey,
  from: string,
  to: string,
): { fromTime: string; toTime: string; groupBy: string } {
  if (period === 'custom') {
    return {
      fromTime: new Date(from).toISOString(),
      toTime: new Date(to).toISOString(),
      groupBy: 'day',
    };
  }
  const opt = PERIOD_OPTIONS.find(p => p.key === period)!;
  const now = new Date();
  return {
    fromTime: new Date(now.getTime() - opt.offsetMs).toISOString(),
    toTime: now.toISOString(),
    groupBy: opt.groupBy,
  };
}

function normalizeToBucket(timestamp: string, groupBy: string): string {
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return timestamp;
  switch (groupBy) {
    case 'minute15': {
      const m = Math.floor(d.getMinutes() / 15) * 15;
      d.setMinutes(m, 0, 0);
      break;
    }
    case 'hour':
      d.setMinutes(0, 0, 0);
      break;
    case 'day':
      d.setHours(0, 0, 0, 0);
      break;
    case 'month':
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
      break;
  }
  return d.toISOString();
}

function formatBucketLabel(timestamp: string, groupBy: string): string {
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return timestamp;
  switch (groupBy) {
    case 'minute15':
    case 'hour':
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case 'day':
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    case 'month':
      return d.toLocaleDateString([], { month: 'short', year: 'numeric' });
    default:
      return timestamp;
  }
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export const InstanceDashboardPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();

  const [instance, setInstance] = useState<Instance | null>(null);
  const [loginMetrics, setLoginMetrics] = useState<LoginMetrics | null>(null);
  const [txEntries, setTxEntries] = useState<TransactionsByTypeEntry[]>([]);

  const [loadingInstance, setLoadingInstance] = useState(true);
  const [loadingLogins, setLoadingLogins] = useState(true);
  const [loadingTx, setLoadingTx] = useState(true);

  const [instanceError, setInstanceError] = useState<string | null>(null);
  const [loginsError, setLoginsError] = useState<string | null>(null);
  const [txError, setTxError] = useState<string | null>(null);

  const [loginPeriod, setLoginPeriod] = useState<PeriodKey>('last7d');
  const [loginCustomFrom, setLoginCustomFrom] = useState('');
  const [loginCustomTo, setLoginCustomTo] = useState('');
  const [appliedLoginCustomFrom, setAppliedLoginCustomFrom] = useState('');
  const [appliedLoginCustomTo, setAppliedLoginCustomTo] = useState('');

  const [period, setPeriod] = useState<PeriodKey>('last7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedCustomFrom, setAppliedCustomFrom] = useState('');
  const [appliedCustomTo, setAppliedCustomTo] = useState('');

  useEffect(() => {
    if (!instanceId) return;

    api.getInstanceById(instanceId)
      .then(setInstance)
      .catch(() => setInstanceError('Failed to load instance.'))
      .finally(() => setLoadingInstance(false));
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return;
    if (loginPeriod === 'custom' && (!appliedLoginCustomFrom || !appliedLoginCustomTo)) return;

    const { fromTime, toTime } = getPeriodParams(loginPeriod, appliedLoginCustomFrom, appliedLoginCustomTo);

    setLoadingLogins(true);
    setLoginsError(null);

    api.getLoginMetrics(instanceId, { fromTime, toTime })
      .then(setLoginMetrics)
      .catch(() => setLoginsError('Failed to load login metrics.'))
      .finally(() => setLoadingLogins(false));
  }, [instanceId, loginPeriod, appliedLoginCustomFrom, appliedLoginCustomTo]);

  useEffect(() => {
    if (!instanceId) return;
    if (period === 'custom' && (!appliedCustomFrom || !appliedCustomTo)) return;

    const params = getPeriodParams(period, appliedCustomFrom, appliedCustomTo);

    setLoadingTx(true);
    setTxError(null);

    api.getTransactionsByType(instanceId, params)
      .then(setTxEntries)
      .catch(() => setTxError('Failed to load transaction data.'))
      .finally(() => setLoadingTx(false));
  }, [instanceId, period, appliedCustomFrom, appliedCustomTo]);

  const validEntries = txEntries.filter(entry => entry != null);
  const txTypes = validEntries.map(entry => entry.object);

  const currentGroupBy = period === 'custom'
    ? 'day'
    : PERIOD_OPTIONS.find(p => p.key === period)!.groupBy;

  const bucketMap = new Map<string, Record<string, number>>();
  validEntries.forEach(entry => {
    entry.detail.forEach(d => {
      const key = normalizeToBucket(d.timestamp, currentGroupBy);
      if (!bucketMap.has(key)) bucketMap.set(key, {});
      const bucket = bucketMap.get(key)!;
      bucket[entry.object] = (bucket[entry.object] ?? 0) + 1;
    });
  });

  const chartData = Array.from(bucketMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([timestamp, counts]) => ({ bucket: timestamp, ...counts }));

  const instanceName = instance?.name ?? instanceId;

  return (
    <div className="instance-dashboard-page">
      <div className="page-header">
        <Link to={`/instances/${instanceId}`} className="back-link">
          ← Back to Instance
        </Link>
        <div className="header-content">
          {loadingInstance ? (
            <h2>Dashboard</h2>
          ) : (
            <h2>Dashboard — {instanceName}</h2>
          )}
          {instance && (
            <span className={`status-badge ${instance.validated ? 'validated' : 'not-validated'}`}>
              {instance.validated ? 'Validated' : 'Not Validated'}
            </span>
          )}
        </div>
        {instanceError && <p className="section-error">{instanceError}</p>}
      </div>

      {/* ---- Login Metrics Section ---- */}
      <section className="dashboard-section">
        <div className="section-header">
          <h3 className="section-title">Login Activity</h3>
          <div className="period-selector">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`period-btn${loginPeriod === opt.key ? ' active' : ''}`}
                onClick={() => setLoginPeriod(opt.key)}
              >
                {opt.label}
              </button>
            ))}
            <button
              className={`period-btn${loginPeriod === 'custom' ? ' active' : ''}`}
              onClick={() => setLoginPeriod('custom')}
            >
              Custom
            </button>
          </div>
        </div>

        {loginPeriod === 'custom' && (
          <div className="custom-range">
            <label className="custom-range-label">
              From
              <input
                type="datetime-local"
                className="custom-range-input"
                value={loginCustomFrom}
                onChange={e => setLoginCustomFrom(e.target.value)}
              />
            </label>
            <label className="custom-range-label">
              To
              <input
                type="datetime-local"
                className="custom-range-input"
                value={loginCustomTo}
                onChange={e => setLoginCustomTo(e.target.value)}
              />
            </label>
            <button
              className="apply-btn"
              disabled={!loginCustomFrom || !loginCustomTo}
              onClick={() => {
                setAppliedLoginCustomFrom(loginCustomFrom);
                setAppliedLoginCustomTo(loginCustomTo);
              }}
            >
              Apply
            </button>
          </div>
        )}

        {loadingLogins && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading login metrics...</p>
          </div>
        )}

        {!loadingLogins && loginsError && (
          <p className="section-error">{loginsError}</p>
        )}

        {!loadingLogins && !loginsError && loginMetrics && (
          <div className="metrics-cards">
            <div className="metric-card">
              <span className="metric-label">Total Login Attempts</span>
              <span className="metric-value">{loginMetrics.attempted.toLocaleString()}</span>
            </div>
            <div className="metric-card success">
              <span className="metric-label">Successful Logins</span>
              <span className="metric-value">{loginMetrics.successful.toLocaleString()}</span>
            </div>
            <div className="metric-card danger">
              <span className="metric-label">Failed Logins</span>
              <span className="metric-value">{(loginMetrics.attempted - loginMetrics.successful).toLocaleString()}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Last Successful Login</span>
              <span className="metric-value date">{formatDate(loginMetrics.lastSuccessful)}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Last Failed Login</span>
              <span className="metric-value date">{formatDate(loginMetrics.lastFailure)}</span>
            </div>
          </div>
        )}
      </section>

      {/* ---- Transactions by Type Section ---- */}
      <section className="dashboard-section">
        <div className="section-header">
          <h3 className="section-title">Transactions by Type</h3>
          <div className="period-selector">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`period-btn${period === opt.key ? ' active' : ''}`}
                onClick={() => setPeriod(opt.key)}
              >
                {opt.label}
              </button>
            ))}
            <button
              className={`period-btn${period === 'custom' ? ' active' : ''}`}
              onClick={() => setPeriod('custom')}
            >
              Custom
            </button>
          </div>
        </div>

        {period === 'custom' && (
          <div className="custom-range">
            <label className="custom-range-label">
              From
              <input
                type="datetime-local"
                className="custom-range-input"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
              />
            </label>
            <label className="custom-range-label">
              To
              <input
                type="datetime-local"
                className="custom-range-input"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
              />
            </label>
            <button
              className="apply-btn"
              disabled={!customFrom || !customTo}
              onClick={() => {
                setAppliedCustomFrom(customFrom);
                setAppliedCustomTo(customTo);
              }}
            >
              Apply
            </button>
          </div>
        )}

        {loadingTx && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading transaction data...</p>
          </div>
        )}

        {!loadingTx && txError && (
          <p className="section-error">{txError}</p>
        )}

        {!loadingTx && !txError && chartData.length === 0 && (
          <p className="no-data">No data is available.</p>
        )}

        {!loadingTx && !txError && chartData.length > 0 && (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  tickFormatter={value => formatBucketLabel(value, currentGroupBy)}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #dee2e6' }}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  labelFormatter={label => formatBucketLabel(label as string, currentGroupBy)}
                />
                {txTypes.map((type, index) => (
                  <Bar
                    key={type}
                    dataKey={type}
                    stackId="stack"
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <ul className="chart-legend">
              {txTypes.map((type, index) => (
                <li key={type} className="chart-legend-item">
                  <span
                    className="chart-legend-swatch"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  {type}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
};
